import { useEffect, useState } from "react";
import { Mic, MicOff, PhoneOff } from "lucide-react";
import { VoiceCallDisplay } from "../../utils/voiceCall";
import { getVoiceInitials } from "../../utils/voiceCall";
import {
  Avatar,
  AvatarWrap,
  ContactMeta,
  Controls,
  HangUpButton,
  HangUpLabel,
  Header,
  IconButton,
  Overlay,
  Panel,
  SecondaryControls,
  StatusDot,
  StatusRow,
  Timer,
} from "./styles";

type Props = {
  contact: VoiceCallDisplay;
  status: "connecting" | "in-call";
  callStartedAt: number | null;
  muted?: boolean;
  onToggleMute?: () => void;
  onHangUp: () => void;
};

function formatDuration(totalSeconds: number) {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;

  return [h, m, s].map((part) => String(part).padStart(2, "0")).join(":");
}

export function ActiveVoiceCallPanel({
  contact,
  status,
  callStartedAt,
  muted = false,
  onToggleMute,
  onHangUp,
}: Props) {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    if (status !== "in-call" || !callStartedAt) {
      setElapsedSeconds(0);
      return;
    }

    const tick = () => {
      setElapsedSeconds(
        Math.max(0, Math.floor((Date.now() - callStartedAt) / 1000)),
      );
    };

    tick();
    const intervalId = window.setInterval(tick, 1000);
    return () => window.clearInterval(intervalId);
  }, [callStartedAt, status]);

  const isConnected = status === "in-call";

  return (
    <Overlay role="dialog" aria-modal="true" aria-label="Ligação em andamento">
      <Panel>
        <Header>
          <span>Ligação com</span>
          <strong>{contact.name}</strong>
          <StatusRow>
            <StatusDot $live={isConnected} />
            {isConnected ? "Conectado" : "Conectando..."}
          </StatusRow>
        </Header>

        <AvatarWrap>
          <Avatar $photo={Boolean(contact.photoUrl)}>
            {contact.photoUrl ? (
              <img src={contact.photoUrl} alt={contact.name} />
            ) : (
              getVoiceInitials(contact.name)
            )}
          </Avatar>
        </AvatarWrap>

        <ContactMeta>
          <strong>{contact.name}</strong>
          <span>Ligação de voz Auge</span>
        </ContactMeta>

        <Timer>
          {isConnected ? formatDuration(elapsedSeconds) : "00:00:00"}
        </Timer>

        <Controls>
          <SecondaryControls>
            {onToggleMute ? (
              <IconButton
                type="button"
                aria-label={muted ? "Ativar microfone" : "Silenciar"}
                $active={muted}
                onClick={onToggleMute}
              >
                {muted ? <MicOff size={22} /> : <Mic size={22} />}
              </IconButton>
            ) : null}
          </SecondaryControls>

          <HangUpButton type="button" aria-label="Desligar" onClick={onHangUp}>
            <PhoneOff size={28} />
          </HangUpButton>
          <HangUpLabel>Desligar</HangUpLabel>
        </Controls>
      </Panel>
    </Overlay>
  );
}
